<div class="modal-header user-deletion-error-header">
  <h4 class="modal-title d-flex align-items-center">
    <i [class]="getErrorIcon()" class="error-icon mr-3"></i>
    {{ getErrorTitle() }}
  </h4>
  <button type="button" class="close" aria-label="Close" (click)="close()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>

<div class="modal-body user-deletion-error-body">
  <!-- Loading State -->
  <div *ngIf="loading" class="text-center py-4">
    <div class="spinner-border text-primary" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <p class="mt-2">Loading information...</p>
  </div>

  <!-- Error Description -->
  <div *ngIf="!loading && !showReassignmentStep" class="error-description">
    <div class="alert alert-warning d-flex align-items-start">
      <i class="fas fa-exclamation-triangle mr-3 mt-1"></i>
      <div>
        <strong>Action Required</strong>
        <p class="mb-0 mt-2">{{ getErrorDescription() }}</p>
      </div>
    </div>

    <!-- Primary Contact Specific Information -->
    <div *ngIf="error?.type === 'primary_contact'" class="primary-contact-details mt-4">
      <h6 class="font-weight-bold mb-3">
        <i class="fas fa-building mr-2"></i>
        Affected Clients ({{ clients.length }})
      </h6>

      <div *ngIf="clients.length > 0" class="client-list">
        <div *ngFor="let client of clients" class="client-item d-flex align-items-center mb-2 p-3 border rounded">
          <div class="client-avatar mr-3">
            <i class="fas fa-building text-primary"></i>
          </div>
          <div class="client-info flex-grow-1">
            <strong>{{ client.client_organization_name || client.organization_name || 'Unknown Client' }}</strong>
            <div class="text-muted small">{{ client.primary_contact?.email || 'No email provided' }}</div>
          </div>
          <div class="primary-contact-badge">
            <span class="badge badge-warning">Primary Contact</span>
          </div>
        </div>
      </div>

      <!-- Solution Steps -->
      <div class="solution-steps mt-4">
        <h6 class="font-weight-bold mb-3">
          <i class="fas fa-lightbulb mr-2"></i>
          How to Fix This
        </h6>

        <div class="steps-container">
          <div class="step-item d-flex align-items-start mb-3">
            <div class="step-number">1</div>
            <div class="step-content">
              <strong>Reassign Primary Contact</strong>
              <p class="mb-0 text-muted">
                Assign another user as the primary contact for the affected clients.
              </p>
            </div>
          </div>

          <div class="step-item d-flex align-items-start mb-3">
            <div class="step-number">2</div>
            <div class="step-content">
              <strong>Delete User</strong>
              <p class="mb-0 text-muted">
                Once reassigned, you'll be able to delete the user successfully.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Organization Admin Specific Information -->
    <div *ngIf="error?.type === 'organization_admin'" class="admin-details mt-4">
      <div class="alert alert-info">
        <i class="fas fa-info-circle mr-2"></i>
        <strong>Administrator Protection:</strong> Company Administrators have elevated privileges and can only be deleted by System Administrators to prevent accidental removal of critical access.
      </div>
    </div>

    <!-- Self Deletion Information -->
    <div *ngIf="error?.type === 'self_deletion'" class="self-deletion-details mt-4">
      <div class="alert alert-info">
        <i class="fas fa-info-circle mr-2"></i>
        <strong>Security Measure:</strong> Users cannot delete their own accounts to prevent accidental lockouts. Contact another administrator if you need your account removed.
      </div>
    </div>
  </div>

  <!-- Reassignment Step -->
  <div *ngIf="!loading && showReassignmentStep" class="reassignment-step">
    <div class="step-header mb-4">
      <h5 class="font-weight-bold">
        <i class="fas fa-user-edit mr-2"></i>
        Reassign Primary Contact
      </h5>
      <p class="text-muted">Select a new primary contact for the affected clients.</p>
    </div>

    <div class="form-group">
      <label for="replacementUser" class="font-weight-bold">New Primary Contact</label>
      <select
        id="replacementUser"
        class="form-control"
        [(ngModel)]="selectedReplacementUser"
        [disabled]="loading">
        <option value="">Select a user...</option>
        <option *ngFor="let user of organizationUsers" [value]="user.id">
          {{ user.first_name }} {{ user.last_name }} ({{ user.email }})
        </option>
      </select>
      <small class="form-text text-muted">
        This user will become the primary contact for all {{ clients.length }} affected client{{ clients.length > 1 ? 's' : '' }}.
      </small>
    </div>

    <div class="affected-clients-summary mt-3">
      <h6 class="font-weight-bold">Clients to be reassigned:</h6>
      <ul class="list-unstyled">
        <li *ngFor="let client of clients" class="mb-1">
          <i class="fas fa-building mr-2 text-primary"></i>
          {{ client.client_organization_name || client.organization_name || 'Unknown Client' }}
        </li>
      </ul>
    </div>
  </div>
</div>

<div class="modal-footer user-deletion-error-footer">
  <!-- Initial State Buttons -->
  <div *ngIf="!showReassignmentStep" class="w-100">
    <div *ngIf="error?.type === 'primary_contact' && canReassign()" class="d-flex justify-content-between">
      <button type="button" class="btn btn-secondary" (click)="close()">
        <i class="fas fa-times mr-2"></i>
        Cancel
      </button>
      <button type="button" class="btn btn-primary" (click)="showReassignmentOptions()">
        <i class="fas fa-user-edit mr-2"></i>
        Reassign Primary Contact
      </button>
    </div>

    <div *ngIf="error?.type !== 'primary_contact' || !canReassign()" class="text-center">
      <button type="button" class="btn btn-secondary" (click)="close()">
        <i class="fas fa-check mr-2"></i>
        Understood
      </button>
    </div>
  </div>

  <!-- Reassignment State Buttons -->
  <div *ngIf="showReassignmentStep" class="d-flex justify-content-between w-100">
    <button type="button" class="btn btn-secondary" (click)="showReassignmentStep = false" [disabled]="loading">
      <i class="fas fa-arrow-left mr-2"></i>
      Back
    </button>
    <button
      type="button"
      class="btn btn-success"
      (click)="reassignPrimaryContact()"
      [disabled]="!selectedReplacementUser || loading">
      <i class="fas fa-check mr-2" *ngIf="!loading"></i>
      <span class="spinner-border spinner-border-sm mr-2" *ngIf="loading"></span>
      {{ loading ? 'Reassigning...' : 'Confirm Reassignment' }}
    </button>
  </div>
</div>
